import {
  type AvailableReviewersResponse,
  AvailableReviewersResponseSchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/available_reviewers'
import {keepPreviousData, useQuery, type QueryKey} from '@github-ui/react-query'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'

function availableReviewersQueryKey(apiUrl: string): QueryKey {
  return [PageData.availableReviewers, apiUrl]
}

/**
 * Loads the reviewer typeahead suggestions from
 * `GET /pull/:id/page_data/reviewers?q=<query>`. When `query` is empty, the
 * server still returns a capped initial set of users.
 *
 * The backend serves proto3-JSON, which `queryFnFetch` decodes through
 * `AvailableReviewersResponseSchema`. Previous results are kept while a new
 * request is in flight so the picker doesn't flash to an empty state as the
 * user types.
 */
export function useAvailableReviewers({query, enabled = true}: {query?: string; enabled?: boolean} = {}) {
  const params = query ? `q=${encodeURIComponent(query)}` : undefined
  const apiUrl = usePageDataUrl(PageData.availableReviewers, params)
  return useQuery<AvailableReviewersResponse>({
    queryKey: availableReviewersQueryKey(apiUrl),
    queryFn: () =>
      queryFnFetch(
        {queryDeps: {pathname: apiUrl}, routeId: 'pull-requests', queryName: 'availableReviewers'},
        {schema: AvailableReviewersResponseSchema},
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  })
}
